import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AngularMaterialModule } from 'src/app/material.module';
import { getNeoCashOutFinalCashAmount, getNeoCashOutServiceFee, getNeoCashOutWalletCredit, INeoCashOutVoucherDetails } from '@lib/interfaces/card-less.interface';
import { AccountsService } from '@lib/services';

export type NeoCashOutResultStatus = 'REDEEMED' | 'DECLINED';

@Component({
    selector: 'app-cash-out-result',
    standalone: true,
    templateUrl: './cash-out-result.component.html',
    styleUrls: ['./cash-out-result.component.scss'],
    imports: [AngularMaterialModule],
})
export class CashOutResultComponent {
    voucherDetails: INeoCashOutVoucherDetails;
    status: NeoCashOutResultStatus = 'REDEEMED';
    protected readonly today = new Date();
    protected loading = false;

    @ViewChild('receipt', { static: false }) receiptEl: ElementRef;

    constructor(private readonly dialogRef: MatDialogRef<CashOutResultComponent>, private readonly accountsService: AccountsService) {}

    protected get customerInitials(): string {
        const { first_name, last_name } = this.voucherDetails.user;
        return `${first_name?.charAt(0) ?? ''}${last_name?.charAt(0) ?? ''}`.toUpperCase();
    }

    protected get customerFullName(): string {
        return `${this.voucherDetails.user.first_name} ${this.voucherDetails.user.last_name}`.trim();
    }

    protected get serviceFee(): number {
        return getNeoCashOutServiceFee(this.voucherDetails);
    }

    protected get finalCashAmount(): number {
        return getNeoCashOutFinalCashAmount(this.voucherDetails);
    }

    protected get walletCredit(): number {
        return getNeoCashOutWalletCredit(this.voucherDetails);
    }

    // Same client-side render-to-PDF approach as the existing FIB cash flow
    // (src/app/pages/cash/component/receipt/receipt.component.ts) — no backend receipt-download
    // endpoint was shared for this flow.
    protected downloadReceipt(): void {
        this.loading = true;
        html2canvas(this.receiptEl.nativeElement)
            .then((canvas) => {
                const doc = new jsPDF('p', 'mm', 'a4');
                const imgData = canvas.toDataURL('image/png');
                doc.addImage(imgData, 'PNG', 10, 10, 180, 280);
                doc.save(`cash-out-${this.voucherDetails.reference_number}.pdf`);
            })
            .finally(() => (this.loading = false));
    }

    // NEOIQ-2694: a successful redeem moves money out of the agent's wallet and creates a
    // transaction, so closing this screen (the only way to dismiss it) must refresh both on Home —
    // same reloadWalletList signal already used by the Top-up for Agent result screen.
    protected done(): void {
        if (this.status === 'REDEEMED') {
            this.accountsService.reloadWalletList.emit(true);
        }
        this.dialogRef.close();
    }
}
